import { FIXED_MEDIA_HEIGHT } from "../../EditLayout/mediaSpecs";
import { HeaderMediaFrame } from "./getPrimaryHeaderMediaUrl";
import { css } from "@emotion/react";
import { Carousel, styleUtils } from "@minted/minted-components";
import React, { memo } from "react";

const AUTO_ADVANCE_MS = 5_000;
const PREFERS_REDUCED_MOTION = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

const styles = {
  image: css`
    display: block;
    height: ${styleUtils.rem(FIXED_MEDIA_HEIGHT)};
    object-fit: contain;
    width: 100%;
  `,
  viewport: css`
    margin-bottom: ${styleUtils.rem(30)};
    position: relative;
    width: 100%;

    /* Carousel has no prop to hide the arrows yet */
    button[aria-label="Next Slide"],
    button[aria-label="Previous Slide"] {
      display: none;
    }
  `,
};

export type SlideshowMediaProps = {
  alt: string;
  frames: HeaderMediaFrame[];
};

/**
 * Slideshow media treatment — one full-width photo at a time, auto-advancing
 * via the minted-components Carousel (which pauses on hover). Reduced motion
 * turns off auto-advance.
 */
const SlideshowMedia = ({ alt, frames }: SlideshowMediaProps) => {
  if (frames.length === 0) {
    return null;
  }

  return (
    <div css={styles.viewport} data-cy="slideshowMedia">
      <Carousel
        autoplay={!PREFERS_REDUCED_MOTION}
        autoplaySpeed={AUTO_ADVANCE_MS}
        fullWidth
        slideIndicatorPlacement="inside"
        wrap
      >
        {frames.map((frame, index) => (
          <img
            alt={alt}
            css={styles.image}
            data-cy="slideshowImage"
            data-slot-index={frame.position}
            key={`${frame.url}-${frame.position}-${index}`}
            src={frame.url}
          />
        ))}
      </Carousel>
    </div>
  );
};

const MemoizedSlideshowMedia = memo(SlideshowMedia);

export { MemoizedSlideshowMedia as SlideshowMedia };
